import React, { lazy, Suspense, useContext, useEffect, useState } from 'react'
import { Icon, Text, useIsMobile } from '@amzn/storm-ui'
import { LanguagesSupported, Locale } from '../../generated/graphql'
import { ReferrerContext } from '@amzn/aapn-react-components'
import { getAwardLocaleDetails } from '../../util/partner_helper'
import { AapnLink } from '../../shared/components/aapn_routing/aapn_routing'
import {
  CommonWidthContainer,
  CompareCardsContainer,
  DashFrame,
  GrayAapnLink,
  InvisibleContainer,
  LegendsAndCardsContainer,
  LinkContainer,
  LinkContainerWrapper,
  MobileComparisonDetailsWrapper,
  MobileLinkContainer,
  NextIconWrapper,
  PageTitleText,
  PartnerComparisonContentWrapper,
  PartnerComparisonDetailsWrapper,
  PrevIconWrapper,
  RecognitionImage,
  RecognitionsContainer,
  ScrollToTopButton,
  SliderDashFrame,
  SliderItemWrapper,
  SliderText,
  SliderWrapper,
  StickyComparisonHeader,
  StickyCtaLink,
  StickyMicroCardsContainer,
  StickyMicroPartnerCardContainer,
  StyledInlineExpander,
  UnsupportedAlert,
} from './partner_comparison_details.styles'
import PartnerComparisonBackgroundImg from 'assets/images/hero_partner_comparison.svg'
import { EmptyPartnerComparisonCard } from './empty_partner_comparison_card'
import { Legends } from '../directory/components/legends/legends'
import { MetricEvent } from '../../util/click_metrics_provider'
import { ComparedPartnersContext, MAX_COMPARED_PARTNER_COUNT, PartnerDetails } from '../directory/components/partner_comparison/compared_partners_context'
import { PartnerComparisonCard, PartnerComparisonCardProps } from './components/partner_comparison_card/partner_comparison_card'
import { CustomArrowProps, Settings } from 'react-slick'
import { buildLogo } from '../../shared/components/shared_components'
import { StyledDivider } from '../../shared/components/shared_components.styles'
import { useTranslation } from 'react-i18next'
import { directoryStrings } from '../directory/directory_i18n_mappings'
import { MicroPartnerCompareCard } from '../directory/components/cards/micro_partner_card/micro_partner_card'
import { PartnerComparisonTable, PartnerOffers } from './components/partner_comparison_table/partner_comparison_table'
import { DISPLAYED_COMPANY_SERVICES_PROVIDED, DISPLAYED_PRODUCTS_SUPPORTED, DISPLAYED_SUPPORTED_MARKETPLACES_GROUPED } from '../../util/filter_menu_helper'
import {
  CERTIFICATIONS_SUPPORTED_LEVEL_TOKENS,
  CERTIFICATIONS_SUPPORTED_TOKENS,
  COMPANY_SERVICES_PROVIDED_GROUP_TOKENS,
  COMPANY_SERVICES_PROVIDED_TOKENS,
  MARKETPLACES_SUPPORTED_GROUP_TOKENS,
  PRODUCTS_SUPPORTED_GROUP_TOKENS,
  PRODUCTS_SUPPORTED_TOKENS,
  MARKETPLACES_SUPPORTED_TOKENS,
} from '../../shared/shared_i18n_mappings'
import { CertificationsComparisonTable } from './components/partner_comparison_table/certifications_comparison_table'
import { MoreDetailsComparisonTable } from './components/partner_comparison_table/more_details_comparison_table'
import _ from 'lodash'
import { LopContext } from '../../util/language_preference_provider/language_preference_provider'
import { SUPPORTED_LANGUAGES_LOCALES_MAP } from '../../util/locale_helper'
import { useLimitedCallback } from '../../util/use_limited_callback'
import { getMetricsPublisherChainAsync } from '@amzn/aapn-front-end-metrics'
import {
  clickPartnerComparisonDetailsMetricHandler,
  navigateSliderMetricHandler,
  viewPartnerOnMobileMetricHandler,
} from '../../util/partner_comparison_metrics_helper'
import { MicroEmptyCtaText } from '../directory/components/partner_comparison/empty_compare_container.styles'
import { SEARCH_AND_FILTERS_SESSION_KEY } from '../../shared/constants'
import { getNumServices } from '../directory/shared'
import { SearchRefContext } from 'util/search_ref_provider/search_ref_provider'
import { RecognitionsComparisonTable } from './components/partner_comparison_table/recognitions_comparison_table'

const LazySlider = lazy(() => import('react-slick'))
const LazySliderStyles = lazy(() => import('./slider_styles').then(module => ({ default: module.SliderStyles })))

export const COMPARE_PARTNERS_SEARCH_PARAM = 'comparedPartnerIds'

export const PartnerComparisonDetails: React.FunctionComponent = (): JSX.Element => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const referrer = useContext(ReferrerContext).referrer ?? ''
  const refTag = useContext(ReferrerContext).refTag
  const searchRefTag = useContext(SearchRefContext).searchRefTag
  const { selectedPartners } = useContext(ComparedPartnersContext)
  const [isRecognitionsOpen, setIsRecognitionsOpen] = useState(true)
  const [activePartnerSlideIndex, setActivePartnerSlideIndex] = useState(0)
  const languageOfPrefSelection = useContext(LopContext)
  const [isLanguageNotSupported, setIsLanguageNotSupported] = useState(false)
  const supportedMarketplacesGrouped = DISPLAYED_SUPPORTED_MARKETPLACES_GROUPED

  const onView = useLimitedCallback(
    () =>
      getMetricsPublisherChainAsync('PartnerComparisonDetailsPage').then(p =>
        p
          .publishCounter('PartnerComparisonDetailsPage.view', 1)
          .publishString('referrer', referrer)
          .publishString('refTag', refTag)
          .publishString('searchRefTag', searchRefTag)
          .publishString('type', 'PartnerComparisonDetailsEntry'),
      ),
    1,
  )

  const clickAddPartnerMetrics = (): void => {
    clickPartnerComparisonDetailsMetricHandler('partnerComparison.page.add.click')
  }

  const handleClickSlider = (nextIndex: number): void => {
    selectedPartners[nextIndex]?.id &&
      viewPartnerOnMobileMetricHandler(
        'PartnerComparisonDetailsMobile',
        'partnerComparison.mobile.navigatedView.partnerId',
        selectedPartners[nextIndex]?.id ?? '',
      )
    setActivePartnerSlideIndex(nextIndex)
  }

  const resultsScrollHandler = (): void => {
    const partnerRemoveButtonTop = document.getElementById('remove-partner-button')?.getBoundingClientRect().top ?? 0
    const removeButtonPosition = partnerRemoveButtonTop + window.pageYOffset
    const partnerCompareStickyHeaderContainer = document.getElementById('partner-comparison-sticky-header')
    // Applying Math.ceil to account for subpixel bug in Chrome: https://stackoverflow.com/questions/52470549/subpixel-scroll-issue-cant-set-scrolltop-properly-on-chrome-69
    if (removeButtonPosition && partnerCompareStickyHeaderContainer && Math.ceil(window.pageYOffset) >= Math.ceil(removeButtonPosition)) {
      partnerCompareStickyHeaderContainer?.classList.remove('hidden-partner-compare-sticky-header')
      partnerCompareStickyHeaderContainer?.classList.add('displayed-partner-compare-sticky-header')
    } else {
      if (removeButtonPosition && partnerCompareStickyHeaderContainer) {
        partnerCompareStickyHeaderContainer?.classList.add('hidden-partner-compare-sticky-header')
        partnerCompareStickyHeaderContainer?.classList.remove('displayed-partner-compare-sticky-header')
      }
    }
  }

  useEffect(() => {
    const throttledResultsScrollHandler = _.throttle(resultsScrollHandler, 150)
    window.addEventListener('scroll', throttledResultsScrollHandler)
    return () => {
      window.removeEventListener('scroll', throttledResultsScrollHandler)
    }
  }, [])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [])

  const partnerRecognitions = selectedPartners[activePartnerSlideIndex]?.awards?.map((award, index) => {
    return (
      <>
        <StyledDivider key={`${index}-divider`} />
        <RecognitionImage key={`${index}-recognition`} alt={getAwardLocaleDetails(award, Locale.EnUs)?.name} src={award.imageDownloadLocation} />
      </>
    )
  })

  const buildComparedPartner = (partnerDetails: PartnerDetails): PartnerComparisonCardProps => {
    const supportedLocales = partnerDetails?.languages?.map((language: LanguagesSupported) => SUPPORTED_LANGUAGES_LOCALES_MAP.get(language))
    const isLanguageOfPreferenceSupported = !!supportedLocales?.includes(languageOfPrefSelection?.lop)
    return {
      id: partnerDetails?.id ?? '',
      companyName: partnerDetails?.companyName ?? '',
      description: partnerDetails?.description ?? '',
      companyLogo: partnerDetails?.companyLogo ?? '',
      contactUrl: partnerDetails?.contactUrl ?? '',
      accreditation: partnerDetails?.accreditation ?? [],
      numAwards: partnerDetails?.awards?.length ?? 0,
      numProducts: partnerDetails?.products?.length ?? 0,
      numServices: getNumServices(partnerDetails?.services),
      numCertifications: partnerDetails?.certifications?.length ?? 0,
      currentPageSize: partnerDetails?.currentPageSize,
      currentPageNumber: partnerDetails?.currentPageNumber,
      cardIndex: partnerDetails?.cardIndex,
      isLanguageOfPreferenceSupported: isLanguageOfPreferenceSupported,
      setIsLanguageNotSupported: setIsLanguageNotSupported,
      leadChannels: partnerDetails?.leadChannels,
    }
  }

  const partnerCards = selectedPartners?.map((partner: PartnerDetails, index) => {
    const comparedPartner = buildComparedPartner(partner)
    return <PartnerComparisonCard key={index} {...comparedPartner} />
  })

  const emptyCardCount = MAX_COMPARED_PARTNER_COUNT - selectedPartners.length
  const backToDirectoryLink = (
    <AapnLink
      data-testid="compare-page-back-to-directory"
      to={`${sessionStorage.getItem(SEARCH_AND_FILTERS_SESSION_KEY) || ''}`}
      onClick={() => clickPartnerComparisonDetailsMetricHandler('partnerComparison.page.redirectToHomePageLink.click')}
    >
      <LinkContainerWrapper data-id="compare-page-back-to-directory" data-publish-metrics={MetricEvent.Click}>
        <Icon type="angle-inline-start" size="sm" />
        {` ${t(directoryStrings.BACK_TO_DIRECTORY)}`}
      </LinkContainerWrapper>
    </AapnLink>
  )

  const pageTitle = (
    <PageTitleText type={'h1'} styleAs={'h2'}>
      {t(directoryStrings.COMPARE_PARTNERS_TEXT)}
    </PageTitleText>
  )

  const PrevArrow = (props: CustomArrowProps): JSX.Element => {
    const { onClick } = props
    return (
      <PrevIconWrapper onClick={onClick}>
        <Icon
          type={'angle-inline-start'}
          size={'2x'}
          aria-label={t(directoryStrings.NAVIGATE_PREVIOUS_ICON)}
          onClick={() => navigateSliderMetricHandler('PartnerComparisonDetailsMobile', 'partnerComparison.mobile.slider.prevButton.click')}
        />
      </PrevIconWrapper>
    )
  }

  const NextArrow = (props: CustomArrowProps): JSX.Element => {
    const { onClick } = props
    return (
      <NextIconWrapper className={'slick-arrow'} onClick={onClick}>
        <Icon
          type={'angle-inline-end'}
          size={'2x'}
          aria-label={t(directoryStrings.NAVIGATE_NEXT_ICON)}
          onClick={() => navigateSliderMetricHandler('PartnerComparisonDetailsMobile', 'partnerComparison.mobile.slider.nextButton.click')}
        />
      </NextIconWrapper>
    )
  }

  const sliderSettings: Settings = {
    infinite: false,
    dots: true,
    speed: 500,
    arrows: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    beforeChange: (currentIndex: number, nextIndex: number) => handleClickSlider(nextIndex),
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
  }

  const unsupportedLanguageAlert = (
    <UnsupportedAlert type="info" header={t(directoryStrings.BANNER_LANGUAGE_NOT_SUPPORTED)} withCloseButton onClose={() => setIsLanguageNotSupported(false)}>
      <Text>{t(directoryStrings.BANNER_SELECT_ANOTHER_PARTNER)}</Text>
    </UnsupportedAlert>
  )

  const scrollToTop = (): void => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const activePartnerOnMobile = buildComparedPartner(selectedPartners[activePartnerSlideIndex])

  const desktopContent = (
    <PartnerComparisonDetailsWrapper partnerComparisonHeroImg={PartnerComparisonBackgroundImg}>
      {selectedPartners.length > 0 && (
        <StickyComparisonHeader
          id={'partner-comparison-sticky-header'}
          className="hidden-partner-compare-sticky-header"
          data-testid={'partner-compare-sticky-header'}
        >
          <StickyMicroCardsContainer>
            {/*exists to divide the StickyMicroCardsContainer into 4 chunks equally to align with the legends and partner cards in the page's content*/}
            <InvisibleContainer />
            {selectedPartners.map((partner, index) => {
              const comparedPartner = buildComparedPartner(partner)
              return (
                <StickyMicroPartnerCardContainer key={index}>
                  <MicroPartnerCompareCard
                    type={'stickyHeader'}
                    key={index}
                    partnerId={partner.id}
                    partnerName={partner.companyName}
                    partnerAccreditation={partner.accreditation}
                    {...comparedPartner}
                  />
                </StickyMicroPartnerCardContainer>
              )
            })}
            {emptyCardCount > 0 &&
              [...Array(emptyCardCount)].map((_, index) => (
                <StickyCtaLink
                  key={index}
                  data-testid={`cta-link-sticky-header-${index}`}
                  to={`${sessionStorage.getItem(SEARCH_AND_FILTERS_SESSION_KEY) || ''}`}
                  onClick={clickAddPartnerMetrics}
                >
                  <DashFrame key={index}>
                    <MicroEmptyCtaText fontSize={'medium'}>{t(directoryStrings.SELECT_ANOTHER_PARTNER_TO_COMPARE)}</MicroEmptyCtaText>
                  </DashFrame>
                </StickyCtaLink>
              ))}
          </StickyMicroCardsContainer>
        </StickyComparisonHeader>
      )}
      <PartnerComparisonContentWrapper>
        <LinkContainer>{backToDirectoryLink}</LinkContainer>
        {pageTitle}
        {isLanguageNotSupported && unsupportedLanguageAlert}
        <LegendsAndCardsContainer>
          <Legends />
          <CompareCardsContainer>
            {partnerCards}
            {emptyCardCount > 0 && [...Array(emptyCardCount)].map((_, index) => <EmptyPartnerComparisonCard key={index} />)}
          </CompareCardsContainer>
        </LegendsAndCardsContainer>
        {selectedPartners.length > 0 && (
          <>
            <PartnerComparisonTable
              tableLabelKey={directoryStrings.TABLE_SERVICES}
              tableSectionGroup={DISPLAYED_COMPANY_SERVICES_PROVIDED}
              translationGroupTokenMap={COMPANY_SERVICES_PROVIDED_GROUP_TOKENS}
              translationTokenMap={COMPANY_SERVICES_PROVIDED_TOKENS}
              partnerOffersField={PartnerOffers.Services}
              selectedPartners={selectedPartners}
              emptyColumnCount={emptyCardCount}
            />
            <PartnerComparisonTable
              tableLabelKey={directoryStrings.TABLE_PRODUCTS}
              tableSectionGroup={DISPLAYED_PRODUCTS_SUPPORTED}
              translationGroupTokenMap={PRODUCTS_SUPPORTED_GROUP_TOKENS}
              translationTokenMap={PRODUCTS_SUPPORTED_TOKENS}
              partnerOffersField={PartnerOffers.Products}
              selectedPartners={selectedPartners}
              emptyColumnCount={emptyCardCount}
            />
            <PartnerComparisonTable
              tableLabelKey={directoryStrings.TABLE_COUNTRIES}
              tableSectionGroup={supportedMarketplacesGrouped}
              translationGroupTokenMap={MARKETPLACES_SUPPORTED_GROUP_TOKENS}
              translationTokenMap={MARKETPLACES_SUPPORTED_TOKENS}
              partnerOffersField={PartnerOffers.Countries}
              selectedPartners={selectedPartners}
              emptyColumnCount={emptyCardCount}
            />
            <CertificationsComparisonTable
              tableLabelKey={directoryStrings.TABLE_CERTIFICATIONS}
              translationLevelTokenMap={CERTIFICATIONS_SUPPORTED_LEVEL_TOKENS}
              translationTokenMap={CERTIFICATIONS_SUPPORTED_TOKENS}
              selectedPartners={selectedPartners}
              emptyColumnCount={emptyCardCount}
            />
            <MoreDetailsComparisonTable selectedPartners={selectedPartners} emptyColumnCount={emptyCardCount} />
            <RecognitionsComparisonTable selectedPartners={selectedPartners} emptyColumnCount={emptyCardCount} />
          </>
        )}
      </PartnerComparisonContentWrapper>
    </PartnerComparisonDetailsWrapper>
  )

  const mobileContent = (
    <MobileComparisonDetailsWrapper partnerComparisonHeroImg={PartnerComparisonBackgroundImg}>
      <CommonWidthContainer>
        <MobileLinkContainer>{backToDirectoryLink}</MobileLinkContainer>
        {pageTitle}
      </CommonWidthContainer>
      <SliderWrapper data-testid={'partner-comparison-slider'}>
        <Suspense fallback={React.Fragment}>
          <LazySliderStyles>
            <LazySlider {...sliderSettings}>
              {selectedPartners.map((selectedPartner, index) => (
                <SliderItemWrapper key={index} data-testid={`slider-logo-${selectedPartner.id}`}>
                  {buildLogo(selectedPartner?.companyLogo ?? '', t)}
                  <SliderText>{selectedPartner.companyName}</SliderText>
                </SliderItemWrapper>
              ))}
              {[...Array(emptyCardCount)].map((_, index) => (
                <SliderItemWrapper key={index} data-testid={`slider-cta-${index}`}>
                  <GrayAapnLink to={`${sessionStorage.getItem(SEARCH_AND_FILTERS_SESSION_KEY) || ''}`} onClick={clickAddPartnerMetrics}>
                    <SliderDashFrame key={index}>
                      <Icon key={index} type={'plus'} role="img" aria-label={t(directoryStrings.ICON_SELECT_ANOTHER_PARTNER)} />
                    </SliderDashFrame>
                  </GrayAapnLink>
                  <SliderText>{t(directoryStrings.SELECT_ANOTHER_PARTNER)}</SliderText>
                </SliderItemWrapper>
              ))}
            </LazySlider>
          </LazySliderStyles>
        </Suspense>
      </SliderWrapper>
      {isLanguageNotSupported && (
        <CommonWidthContainer>
          {unsupportedLanguageAlert}
          <StyledDivider />
        </CommonWidthContainer>
      )}
      {activePartnerOnMobile?.id && (
        <CommonWidthContainer>
          <PartnerComparisonCard key={activePartnerOnMobile?.id} {...activePartnerOnMobile} />
        </CommonWidthContainer>
      )}

      {selectedPartners.length > 0 && activePartnerOnMobile.id && (
        <>
          <PartnerComparisonTable
            tableLabelKey={directoryStrings.TABLE_SERVICES_WITH_COUNT}
            offerCount={getNumServices([selectedPartners[activePartnerSlideIndex]][0]?.services)}
            tableSectionGroup={DISPLAYED_COMPANY_SERVICES_PROVIDED}
            translationGroupTokenMap={COMPANY_SERVICES_PROVIDED_GROUP_TOKENS}
            translationTokenMap={COMPANY_SERVICES_PROVIDED_TOKENS}
            partnerOffersField={PartnerOffers.Services}
            selectedPartners={[selectedPartners[activePartnerSlideIndex]]}
            emptyColumnCount={0}
          />
          <PartnerComparisonTable
            tableLabelKey={directoryStrings.TABLE_PRODUCTS_WITH_COUNT}
            offerCount={[selectedPartners[activePartnerSlideIndex]][0].products?.length}
            tableSectionGroup={DISPLAYED_PRODUCTS_SUPPORTED}
            translationGroupTokenMap={PRODUCTS_SUPPORTED_GROUP_TOKENS}
            translationTokenMap={PRODUCTS_SUPPORTED_TOKENS}
            partnerOffersField={PartnerOffers.Products}
            selectedPartners={[selectedPartners[activePartnerSlideIndex]]}
            emptyColumnCount={0}
          />
          <PartnerComparisonTable
            tableLabelKey={directoryStrings.TABLE_COUNTRIES_WITH_COUNT}
            offerCount={[selectedPartners[activePartnerSlideIndex]][0].marketplacesServed?.length}
            tableSectionGroup={supportedMarketplacesGrouped}
            translationGroupTokenMap={MARKETPLACES_SUPPORTED_GROUP_TOKENS}
            translationTokenMap={MARKETPLACES_SUPPORTED_TOKENS}
            partnerOffersField={PartnerOffers.Countries}
            selectedPartners={[selectedPartners[activePartnerSlideIndex]]}
            emptyColumnCount={0}
          />
          <CertificationsComparisonTable
            tableLabelKey={directoryStrings.TABLE_CERTIFICATIONS_WITH_COUNT}
            translationLevelTokenMap={CERTIFICATIONS_SUPPORTED_LEVEL_TOKENS}
            translationTokenMap={CERTIFICATIONS_SUPPORTED_TOKENS}
            selectedPartners={[selectedPartners[activePartnerSlideIndex]]}
            emptyColumnCount={0}
            certificationCount={[selectedPartners[activePartnerSlideIndex]][0].certifications?.length}
          />
          <MoreDetailsComparisonTable selectedPartners={[selectedPartners[activePartnerSlideIndex]]} emptyColumnCount={0} />
        </>
      )}
      {activePartnerOnMobile?.numAwards > 0 && (
        <>
          <StyledDivider />
          <CommonWidthContainer>
            {
              <StyledInlineExpander
                label={
                  <Text type={'h3'} textColor={'base'}>
                    {t(directoryStrings.RECOGNITIONS)}
                  </Text>
                }
                isOpen={isRecognitionsOpen}
                handleClick={() => setIsRecognitionsOpen(prev => !prev)}
              >
                <RecognitionsContainer>{partnerRecognitions}</RecognitionsContainer>
              </StyledInlineExpander>
            }
          </CommonWidthContainer>
          <StyledDivider />
        </>
      )}

      <ScrollToTopButton onClick={scrollToTop}>{t(directoryStrings.BACK_TO_TOP)}</ScrollToTopButton>
    </MobileComparisonDetailsWrapper>
  )
  onView()
  return isMobile ? mobileContent : desktopContent
}
